// ⚠️ DIAGNÓSTICO TEMPORAL — borrar junto con src/lib/perf-flags.ts.
//
// Medidor de fluidez en pantalla, para usar DENTRO del webview de Instagram/TikTok
// (donde no hay DevTools). Se monta solo con ?perf=1.
//
// Cómo leerlo mientras haces scroll:
//   fps    → fotogramas del último segundo. 55-60 = fluido; <40 = se nota el tirón.
//   jank   → % de fotogramas lentos (>33 ms) en los últimos 5 s. 0-2% es normal.
//   peor   → el fotograma más lento de los últimos 5 s, en ms.
//   lentos → total de fotogramas lentos desde que se abrió (o desde el último toque).
// Toca el recuadro para poner los contadores a cero y repetir la medición.
//
// El overlay se mide a sí mismo lo mínimo posible: el bucle rAF solo guarda marcas
// de tiempo y el texto se reescribe 2 veces por segundo por DOM directo (sin
// re-render de React), así no contamina lo que estamos midiendo.

import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { activePerfFlags, hasPerfFlag } from "@/lib/perf-flags";

const SLOW_MS = 33.4; // por debajo de 30 fps
const WINDOW_MS = 5000;

export default function PerfProbe() {
  const location = useLocation();
  const boxRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const totalRef = useRef<HTMLDivElement>(null);
  const resetRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!hasPerfFlag("perf")) return;

    let raf = 0;
    let stamps: number[] = [];
    let slowTotal = 0;
    let frameTotal = 0;
    let lastPaint = 0;

    resetRef.current = () => {
      stamps = [];
      slowTotal = 0;
      frameTotal = 0;
    };

    const tick = (t: number) => {
      stamps.push(t);
      // Recorta la ventana deslizante.
      const cut = t - WINDOW_MS;
      if (stamps.length > 2 && stamps[0] < cut) {
        let i = 0;
        while (i < stamps.length && stamps[i] < cut) i++;
        stamps = stamps.slice(Math.max(0, i - 1));
      }
      const n = stamps.length;
      if (n >= 2) {
        const gap = stamps[n - 1] - stamps[n - 2];
        frameTotal++;
        if (gap > SLOW_MS) slowTotal++;
      }

      // Repinta el texto 2×/s como máximo.
      if (t - lastPaint > 500 && n >= 3) {
        lastPaint = t;
        let slowWin = 0;
        let worst = 0;
        for (let i = 1; i < n; i++) {
          const g = stamps[i] - stamps[i - 1];
          if (g > SLOW_MS) slowWin++;
          if (g > worst) worst = g;
        }
        const oneSecAgo = t - 1000;
        let fps = 0;
        for (let i = n - 1; i >= 0 && stamps[i] >= oneSecAgo; i--) fps++;
        const jank = ((slowWin / (n - 1)) * 100).toFixed(1);

        if (lineRef.current) {
          lineRef.current.textContent = `fps ${fps}  ·  jank ${jank}%  ·  peor ${worst.toFixed(0)}ms`;
          lineRef.current.style.color = fps >= 50 ? "#7CFFB2" : fps >= 40 ? "#FFD479" : "#FF8A8A";
        }
        if (totalRef.current) {
          totalRef.current.textContent = `lentos ${slowTotal}/${frameTotal}`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!hasPerfFlag("perf")) return null;

  const off = activePerfFlags().filter((f) => f !== "perf");

  return (
    <div
      ref={boxRef}
      onClick={() => resetRef.current()}
      style={{
        position: "fixed",
        top: "max(4px, env(safe-area-inset-top, 0px))",
        left: 4,
        zIndex: 2147483647,
        // Sin backdrop-filter ni sombras a propósito: el medidor no debe añadir
        // el mismo coste que estamos investigando.
        background: "rgba(8,14,20,0.86)",
        color: "#E6F0F6",
        font: "600 11px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace",
        padding: "5px 8px",
        borderRadius: 7,
        pointerEvents: "auto",
        maxWidth: "72vw",
      }}
    >
      <div ref={lineRef} style={{ color: "#7CFFB2" }}>midiendo…</div>
      <div ref={totalRef} style={{ opacity: 0.75 }}>lentos 0/0</div>
      <div style={{ opacity: 0.6, marginTop: 2 }}>
        {location.pathname}
        {off.length > 0 ? ` · off: ${off.join(",")}` : " · nada desactivado"}
      </div>
    </div>
  );
}
